import React from 'react';
import { Divider, Field, Input, Select, Text } from '@fluentui/react-components';
import { useForm } from 'react-hook-form';
import { useAppActions, useAppState } from '../../context';
import { getValidationState } from './components/formValidation';
import { PageReferenceStep } from '@gkn-ili/views/dist/OrderDocumentation';
import { AppEditDialogActions } from './components/dialogActions';
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogHeader,
  AppDialogSurface,
  DialogOptions,
} from './components/baseComponents';
import { useLargeModalStyles } from './components/commonStyles';

type FormState = { name: string; description: string; previousStep: string };

export default function AppCreateStepModal(props: {
  initialPreviousReference: PageReferenceStep;
  dialogOptions: DialogOptions;
}): JSX.Element {
  const styles = useLargeModalStyles();
  const appState = useAppState();
  const appActions = useAppActions();
  const routing = appState.orderView.routing[props.initialPreviousReference.routingItemKey];

  const formHandlers = useForm<FormState>({
    defaultValues: {
      name: 'New step',
      description: '',
      previousStep: props.initialPreviousReference.stepKey,
    },
  });
  const onSubmit = formHandlers.handleSubmit(async (data) => {
    await appActions.editOrder.createStep(
      props.initialPreviousReference.routingItemKey,
      data.name,
      data.description,
      data.previousStep,
    );
    props.dialogOptions.onCloseDialog();
  });

  return (
    <AppDialogSurface dialogOptions={props.dialogOptions}>
      <form>
        <AppDialogBody dialogOptions={props.dialogOptions}>
          <AppDialogHeader dialogOptions={props.dialogOptions}>Create step</AppDialogHeader>
          <AppDialogContent dialogOptions={props.dialogOptions}>
            <Text>Edit the step details and click save to create the step.</Text>
            <Field label="Step name" required {...getValidationState('name', formHandlers.formState.errors)}>
              <Input {...formHandlers.register('name', { required: true })} name="name" className={styles.field} />
            </Field>
            <Field label="Step description" {...getValidationState('description', formHandlers.formState.errors)}>
              <Input {...formHandlers.register('description')} className={styles.field} />
            </Field>
            <Divider>Organization</Divider>
            <Field
              label="Comes after step"
              required
              {...getValidationState('previousStep', formHandlers.formState.errors)}
            >
              <Select {...formHandlers.register('previousStep', { required: true })}>
                <option value="none">As first</option>
                {Object.entries(routing.steps).map(([, value]) => (
                  <option key={value.key} value={value.key}>
                    {value.name}
                  </option>
                ))}
              </Select>
            </Field>
          </AppDialogContent>
          <AppEditDialogActions
            onCancel={props.dialogOptions.onCloseDialog}
            submitDisabled={formHandlers.formState.isSubmitting}
            onSubmit={onSubmit}
          />
        </AppDialogBody>
      </form>
    </AppDialogSurface>
  );
}
